import DriveFileRenameOutline from '@mui/icons-material/DriveFileRenameOutline';
import { Box, IconButton, ListItemIcon, Menu, MenuItem, Typography } from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { EditDrawerContext } from 'components/@home/@documents/SentDocuments/EditDrawer';
import SentDocumentsToolbar from 'components/@home/@documents/SentDocuments/SentDocumentsToolbar';
import FolderDialog from 'components/@home/@documents/SharedDocuments/FolderDialog';
import { ShareDrawerContext } from 'components/@home/@documents/SharedDocuments/ShareDrawer';
import DeleteDialog from 'components/common/DeleteDialog';
import OmmnioDataGrid from 'components/common/OmmnioDataGrid';
import AccountPlusOutlineIcon from 'mdi-react/AccountPlusOutlineIcon';
import DotsVerticalIcon from 'mdi-react/DotsVerticalIcon';
import EditIcon from 'mdi-react/EditIcon';
import SendIcon from 'mdi-react/SendIcon';
import TrashCanOutlineIcon from 'mdi-react/TrashCanOutlineIcon';
import { func, object, shape, string } from 'prop-types';
import React, { memo, useCallback, useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { I18n } from 'utils/i18n';
import { HR_BOT_APP_ID } from 'services/constants';
import appsAction from 'store/app/entities/apps/action';
import {
  useDeleteDocumentFormatMutation,
  useEditDocumentFormatMutation,
  useListDocumentFormatsQuery,
} from 'store/app/entities/SharedFilesSlice';
import renderDateTimeCell from 'utils/renderDateTimeCell';
import renderPermissionsCell from 'utils/renderPermissionsCell';
import useOpenClose from 'utils/useOpenClose';
import { SendDrawerContext } from 'components/@home/@documents/SentDocuments/SendDrawerContext';

const SentDocumentNameCell = memo(
  ({ params, onEdit, onSend, editDocumentFormat, destroyDocumentFormat }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const { row } = params;
    const shareDrawer = useContext(ShareDrawerContext);
    const [renameIsOpen, renameOpen, renameClose] = useOpenClose();
    const [isDeleteOpen, openDelete, closeDelete] = useOpenClose();

    const handleOpenMenu = useCallback(e => {
      e.stopPropagation();
      setAnchorEl(e.currentTarget);
    }, []);

    const handleCloseMenu = useCallback(() => {
      setAnchorEl(null);
    }, []);

    const handleEdit = useCallback(() => {
      onEdit(row);
      handleCloseMenu();
    }, [row, onEdit, handleCloseMenu]);

    const handleSend = useCallback(() => {
      onSend(row);
      handleCloseMenu();
    }, [row, onSend, handleCloseMenu]);

    const handleShareSubmit = useCallback(
      ({ permissions, teamsRemoved } = {}) => {
        if (!permissions) {
          return;
        }
        editDocumentFormat({ id: row._id, body: { permissions, teamsRemoved } });
      },
      [editDocumentFormat, row._id],
    );

    const handleShare = useCallback(() => {
      shareDrawer.open(row, handleShareSubmit);
      handleCloseMenu();
    }, [row, shareDrawer, handleShareSubmit, handleCloseMenu]);

    const handleDelete = useCallback(() => {
      handleCloseMenu();
      openDelete();
    }, [handleCloseMenu, openDelete]);

    const handleConfirmDelete = useCallback(() => {
      destroyDocumentFormat(row._id);
      closeDelete();
    }, [destroyDocumentFormat, row._id, closeDelete]);

    const handleRename = useCallback(() => {
      renameOpen();
      handleCloseMenu();
    }, [renameOpen, handleCloseMenu]);

    const handleRenameSubmit = useCallback(
      name => {
        renameClose();
        editDocumentFormat({ id: row._id, body: { name } });
      },
      [editDocumentFormat, row._id, renameClose],
    );

    return (
      <>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            minWidth: 0,
            width: '100%',
          }}
        >
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1,
              bgcolor: 'primary.ultraLight',
              color: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: '0 0 auto',
            }}
          >
            <DescriptionOutlinedIcon fontSize="small" />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="body2" noWrap sx={{ fontWeight: 700 }}>
              {params.value}
            </Typography>
            <Typography
              variant="caption"
              noWrap
              sx={{
                color: 'text.secondary',
              }}
            >
              {I18n.t(`SignTracking.Modes.${row.documentSource === 'fixed' ? 'fixed' : 'perSend'}`)}
            </Typography>
          </Box>
          <IconButton
            size="small"
            edge="end"
            onClick={handleOpenMenu}
            aria-label={I18n.t('BulkMessaging.Actions.Menu')}
            sx={{ flex: '0 0 auto', mr: -0.5 }}
          >
            <DotsVerticalIcon />
          </IconButton>
        </Box>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleCloseMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem onClick={handleSend}>
            <ListItemIcon>
              <SendIcon />
            </ListItemIcon>
            <Typography variant="inherit">{I18n.t('FolderCellMenu.Send')}</Typography>
          </MenuItem>
          <MenuItem onClick={handleEdit}>
            <ListItemIcon>
              <EditIcon />
            </ListItemIcon>
            <Typography variant="inherit">{I18n.t('FolderCellMenu.Edit')}</Typography>
          </MenuItem>
          {row.isAdmin && (
            <>
              <MenuItem onClick={handleRename}>
                <ListItemIcon>
                  <DriveFileRenameOutline fontSize="small" />
                </ListItemIcon>
                <Typography variant="inherit">{I18n.t('FolderCellMenu.Rename')}</Typography>
              </MenuItem>
              <MenuItem onClick={handleShare}>
                <ListItemIcon>
                  <AccountPlusOutlineIcon />
                </ListItemIcon>
                <Typography variant="inherit">{I18n.t('FolderCellMenu.Share')}</Typography>
              </MenuItem>
              <MenuItem onClick={handleDelete}>
                <ListItemIcon>
                  <TrashCanOutlineIcon />
                </ListItemIcon>
                <Typography variant="inherit">{I18n.t('FolderCellMenu.Delete')}</Typography>
              </MenuItem>
            </>
          )}
        </Menu>
        <DeleteDialog
          message={I18n.t('FolderCellMenu.ConfirmDelete', { name: row?.name })}
          confirmText={I18n.t('FolderCellMenu.Delete')}
          isOpen={isDeleteOpen}
          onConfirm={handleConfirmDelete}
          onClose={closeDelete}
        >
          <Typography>{I18n.t('FolderCellMenu.WillBeDeleted', { name: row?.name })}</Typography>
        </DeleteDialog>
        <FolderDialog
          folder={row}
          onSubmit={handleRenameSubmit}
          onClose={renameClose}
          open={renameIsOpen}
        />
      </>
    );
  },
);

SentDocumentNameCell.displayName = 'SentDocumentNameCell';

SentDocumentNameCell.propTypes = {
  params: shape({
    row: object.isRequired,
    value: string,
  }).isRequired,
  onEdit: func.isRequired,
  onSend: func.isRequired,
  editDocumentFormat: func.isRequired,
  destroyDocumentFormat: func.isRequired,
};

const SentDocuments = ({ folder }) => {
  const editDrawer = useContext(EditDrawerContext);
  const sendDrawer = useContext(SendDrawerContext);
  const dispatch = useDispatch();
  const app = useSelector(state => state.apps.apps?.[HR_BOT_APP_ID]);
  useEffect(() => {
    if (!app?._id) {
      dispatch(appsAction.load());
    }
  }, [app?._id, dispatch]);
  const {
    data: { documentFormats, totalCount } = {},
    isLoading,
    isUninitialized,
  } = useListDocumentFormatsQuery();

  const [destroyDocumentFormat] = useDeleteDocumentFormatMutation();
  const [editDocumentFormat] = useEditDocumentFormatMutation();

  const handleEditRow = useCallback(
    row => {
      editDrawer.open(row);
    },
    [editDrawer],
  );

  const handleSendRow = useCallback(
    row => {
      sendDrawer.open(row, null);
    },
    [sendDrawer],
  );

  const columns = [
    {
      field: 'name',
      flex: 1.7,
      minWidth: 280,
      editable: false,
      renderCell: params => (
        <SentDocumentNameCell
          params={params}
          onEdit={handleEditRow}
          onSend={handleSendRow}
          editDocumentFormat={editDocumentFormat}
          destroyDocumentFormat={destroyDocumentFormat}
        />
      ),
      headerName: I18n.t('SentDocuments.Table.name'),
    },
    {
      field: 'lastSentAt',
      type: 'string',
      flex: 0.8,
      minWidth: 150,
      editable: false,
      renderCell: renderDateTimeCell,
      headerName: I18n.t('SentDocuments.Table.lastSentAt'),
    },
    {
      field: 'permissions',
      flex: 1,
      minWidth: 180,
      editable: false,
      renderCell: renderPermissionsCell,
      headerName: I18n.t('SentDocuments.Table.SharedWith'),
    },
  ];
  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          p: 2,
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h3">{I18n.t(`DocumentsScene.Folders.${folder}`)}</Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                mt: 0.5,
              }}
            >
              {I18n.t('SentDocuments.Description')}
            </Typography>
          </Box>
          <SentDocumentsToolbar />
        </Box>
        <OmmnioDataGrid
          loading={isLoading || isUninitialized}
          sx={{ flex: 1 }}
          columns={columns}
          rows={documentFormats || []}
          rowCount={totalCount || 0}
          getRowId={r => r._id}
          onRowClick={params => editDrawer.open(params.row)}
          updateHistory
          disableEvenOdd
        />
      </Box>
    </Box>
  );
};

SentDocuments.propTypes = {
  folder: string.isRequired,
};

export default SentDocuments;
